import { useMemo } from 'react';
import { Copy, Check, Square, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge, IconButton } from '@vnetwork/ui/primitives';
import { StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import type { DrmStream } from '@/api/aes';
import { streamStatusTone, STREAM_STATUS_LABEL, kindTone, KIND_LABEL } from '../shared';

const muted = (v: React.ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{v}</span>
);

/** Inline copy button — same affordance as the AES scrambler page. */
function CopyCell({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconButton
      variant="ghost"
      size="sm"
      aria-label={copied ? 'Copied' : 'Copy output URL'}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {copied ? <Check size={14} color="var(--vn-success-solid)" /> : <Copy size={14} />}
    </IconButton>
  );
}

export interface StreamColumnHandlers {
  onStop: (s: DrmStream) => void;
  onDelete: (s: DrmStream) => void;
  busyId?: string | null;
}

/** Column definitions for the streams DataTable (kept out of the list page). */
export function useStreamColumns({
  onStop,
  onDelete,
  busyId,
}: StreamColumnHandlers): ColumnDef<DrmStream, unknown>[] {
  return useMemo<ColumnDef<DrmStream, unknown>[]>(
    () => [
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={streamStatusTone(row.original.status)}>
            {STREAM_STATUS_LABEL[row.original.status]}
          </StatusPill>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={kindTone(row.original.kind)} appearance="subtle" size="sm">
            {KIND_LABEL[row.original.kind]}
          </Badge>
        ),
      },
      {
        id: 'stream',
        header: 'Address',
        accessorKey: 'id',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], fontFamily: 'var(--vn-font-mono)' }}>
            {row.original.id}
          </span>
        ),
      },
      {
        id: 'app',
        header: 'App',
        enableSorting: false,
        cell: ({ row }) => muted(row.original.app),
      },
      {
        id: 'shard',
        header: 'Shard',
        enableSorting: false,
        cell: ({ row }) => muted(row.original.shard ?? '—'),
      },
      {
        id: 'abr',
        header: 'ABR',
        enableSorting: false,
        cell: ({ row }) => {
          const n = row.original.abr_ladder?.length ?? 0;
          return n > 0 ? (
            <Badge tone="neutral" appearance="subtle" size="sm">
              {n} rendition{n === 1 ? '' : 's'}
            </Badge>
          ) : (
            muted('—')
          );
        },
      },
      {
        id: 'output',
        header: 'Output',
        enableSorting: false,
        cell: ({ row }) => {
          const url = row.original.output_url;
          if (!url) return muted('—');
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
              <span
                style={{
                  fontFamily: 'var(--vn-font-mono)',
                  fontSize: 'var(--vn-font-size-sm)',
                  color: 'var(--vn-fg-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 200,
                }}
              >
                {url}
              </span>
              <CopyCell value={url} />
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const s = row.original;
          const busy = busyId === s.id;
          return (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}
              onClick={(e) => e.stopPropagation()}
            >
              {s.status === 'live' && (
                <IconButton aria-label="Stop stream" variant="ghost" size="sm" disabled={busy} onClick={() => onStop(s)}>
                  <Square size={14} />
                </IconButton>
              )}
              <IconButton aria-label="Delete stream" variant="ghost" size="sm" disabled={busy} onClick={() => onDelete(s)}>
                <Trash2 size={15} />
              </IconButton>
            </div>
          );
        },
      },
    ],
    [onStop, onDelete, busyId],
  );
}
